// Deferred loading of the heavy dependencies.
//
// The realtime client is roughly three quarters of the bundle and is useless
// until somebody starts a call; the markdown renderer exists only for an
// optional terms modal most agents never enable. Loading both up front made
// every visitor of every embedding page pay for a call they may never make.
//
// The element therefore never imports either statically. It asks here, and
// this resolves them the first time they are actually needed.

import type { AgentHandle, StartAgentOptions } from "./types.js";

export type ConnectFn = (opts: StartAgentOptions) => Promise<AgentHandle>;
export type MarkdownFn = (input: string) => string;

/**
 * Where sibling chunks live, derived from the running script's own URL.
 *
 * Taken at module-evaluation time because `document.currentScript` is only
 * meaningful while the script is executing. Resolving relative to ourselves
 * rather than a build-time constant means the bundle keeps working from any
 * origin it is served from — the CDN, the API, or a customer's own mirror.
 */
const assetBase = (() => {
  try {
    const src = (document.currentScript as HTMLScriptElement | null)?.src;
    if (src) return new URL(".", src).href;
  } catch {
    // Non-DOM context (SSR import, worker). The provider override below is
    // the supported path there.
  }
  return "";
})();

let connectProvider: (() => Promise<ConnectFn>) | null = null;
let markdownProvider: (() => Promise<MarkdownFn>) | null = null;

/**
 * Supply the dependencies statically instead of fetching them.
 *
 * The ESM build wires this at import time so bundler consumers get one
 * self-contained module and no runtime fetch, while the drop-in script keeps
 * the deferred path.
 */
export function setLazyProviders(providers: {
  connect?: () => Promise<ConnectFn>;
  markdown?: () => Promise<MarkdownFn>;
}): void {
  if (providers.connect) connectProvider = providers.connect;
  if (providers.markdown) markdownProvider = providers.markdown;
}

// Kept in module scope so a second call reuses the in-flight or settled
// import rather than issuing another request.
let connectPromise: Promise<ConnectFn> | null = null;
let markdownPromise: Promise<MarkdownFn> | null = null;

// How many times each chunk has been asked for. Only ever read to build a
// distinct specifier on a retry — see chunkSpecifier.
let connectAttempt = 0;
let markdownAttempt = 0;

export function loadConnect(): Promise<ConnectFn> {
  if (connectProvider) return connectProvider();
  connectPromise ??= importChunk<{ connectAgent: ConnectFn }>("agents-core.mjs", connectAttempt)
    .then((m) => m.connectAgent)
    .catch((err) => {
      // Clear so a retry can succeed: a transient network failure must not
      // leave the button permanently dead for the rest of the page's life.
      connectPromise = null;
      connectAttempt += 1;
      throw err;
    });
  return connectPromise;
}

export function loadMarkdown(): Promise<MarkdownFn> {
  if (markdownProvider) return markdownProvider();
  markdownPromise ??= importChunk<{ markdownToSafeHTML: MarkdownFn }>("agents-markdown.mjs", markdownAttempt)
    .then((m) => m.markdownToSafeHTML)
    .catch((err) => {
      markdownPromise = null;
      markdownAttempt += 1;
      throw err;
    });
  return markdownPromise;
}

/**
 * The URL a given attempt at a chunk should import.
 *
 * A retry gets a DIFFERENT specifier, and that is the whole fix for AIS-5410.
 * Clearing our own cached promise is not enough: the browser's module map
 * remembers a failed dynamic import for the life of the page, so re-importing
 * the same specifier hands back the same rejection without ever going to the
 * network. A visitor who hit a momentary blip was stuck behind a Retry button
 * that could not succeed no matter how healthy the connection became, and only
 * a page reload cleared it — which nothing on screen told them to do.
 *
 * The query is invisible to what serves the file: Bunny ignores query strings
 * in its cache key by default and GCS ignores them entirely, so a retry is a
 * fresh module-map entry rather than a cache miss at the edge.
 *
 * The first attempt is deliberately left clean, so the common path requests
 * exactly the URL the publish pipeline wrote and nothing has to reason about
 * a decorated one.
 */
export function chunkSpecifier(base: string, file: string, attempt: number): string {
  return attempt > 0 ? `${base}${file}?retry=${attempt}` : `${base}${file}`;
}

/**
 * Dynamic import of a sibling chunk. Swappable so a test can drive the real
 * loadConnect/loadMarkdown paths — the ones that actually cache — rather than
 * the injected-provider shortcut, which is what let AIS-5410 ship under a test
 * named for the behaviour it did not exercise.
 */
let chunkImporter = <T>(specifier: string): Promise<T> => import(/* @vite-ignore */ specifier) as Promise<T>;

/** Test seam. Returns a restore function. */
export function __setChunkImporter(fn: <T>(specifier: string) => Promise<T>): () => void {
  const previous = chunkImporter;
  chunkImporter = fn;
  return () => {
    chunkImporter = previous;
  };
}

/** Test seam: forget cached promises and attempt counters. */
export function __resetChunkState(): void {
  connectPromise = null;
  markdownPromise = null;
  connectAttempt = 0;
  markdownAttempt = 0;
  connectProvider = null;
  markdownProvider = null;
}

function importChunk<T>(file: string, attempt: number): Promise<T> {
  return chunkImporter<T>(chunkSpecifier(assetBase, file, attempt));
}
